<script lang="ts">

  import { page } from "$app/state"
  import httpClient from "$lib/axios-client"
  import Loader from "$lib/components/loader.svelte"
  import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "$lib/components/ui/dialog"
  import { Label } from "$lib/components/ui/label"
  import { Separator } from "$lib/components/ui/separator"
  import type { _response } from "$lib/data/type/_response"
  import { createQuery } from "@tanstack/svelte-query"
  import type { Order } from "../_types"

  let {
    open,
    setOpen,
    select,
  }: {
    open: boolean
    setOpen: (value: boolean) => void
    select: string
  } = $props()

  const prefix = $derived(page.params.prefix ?? "0")

  const query = createQuery(() => ({
    queryKey: ["order-detail", prefix, select],
    enabled: open && select !== "",
    placeholderData: (previous) => previous,
    queryFn: async () => {
      const response = await httpClient.get<_response<Order>>(`/api/${prefix}/order/data/${select}`)

      return response.data.result!
    },
  }))

</script>

<Dialog
  open={open}
  onOpenChange={setOpen}>
  <DialogContent
    class="w-sm"
    onOpenAutoFocus={(event) => event.preventDefault()}>
    <DialogHeader>
      <DialogTitle>Detail</DialogTitle>
      <DialogDescription>Informasi order</DialogDescription>
    </DialogHeader>
    <Loader show={query.isFetching}/>
    <div class="space-y-3">
      {#if query.isError}
        <div
          class="py-4 text-center text-sm text-destructive"
          role="alert">
          Detail order gagal dimuat
        </div>
      {/if}
      <div class="grid grid-cols-2 gap-3">
        <div class="flex-1">
          <Label>Tanggal</Label>
          <div class="text-desc font-mono">{query.data?.date.toDateFormat("dd/MM/yyyy") || "-"}</div>
        </div>
        <div class="flex-1">
          <Label>Nomor</Label>
          <div class="text-desc font-mono">{query.data?.number || "-"}</div>
        </div>
      </div>
      <div>
        <Label>Customer</Label>
        <div class="text-desc">{query.data?.customer.name || "-"}</div>
      </div>
      <div>
        <Label>Alamat</Label>
        <div class="text-desc">{query.data?.customer.address || "-"}</div>
      </div>
      <div class="grid grid-cols-2 gap-3">
        <div class="flex-1">
          <Label>Tipe</Label>
          <div class="text-desc">{query.data?.type?.name || "-"}</div>
        </div>
        <div class="flex-1">
          <Label>Price List</Label>
          <div class="text-desc">{query.data?.price_list_name || "-"}</div>
        </div>
      </div>
      <div>
        <Label>Keterangan</Label>
        <div class="text-desc">{query.data?.description || "-"}</div>
      </div>
      <Separator/>
      <div class="grid grid-cols-2 gap-3">
        <div class="flex-1">
          <Label>Sub Total</Label>
          <div class="text-desc font-mono">{(query.data?.total_sub ?? 0).toLocaleString("en-US") ?? "-"}</div>
        </div>
        <div class="flex-1">
          <Label>Diskon</Label>
          <div class="text-desc font-mono">{(query.data?.total_discount ?? 0).toLocaleString("en-US") ?? "-"}</div>
        </div>
      </div>
      <div>
        <Label>Status</Label>
        <div class="text-desc">{query.data?.status.name || "-"}</div>
      </div>
      <div class="flex gap-2">
        <div class="flex-1">
          <Label>Total</Label>
          <div class="text-desc font-mono">{(query.data?.total ?? 0).toLocaleString("en-US") ?? "-"}</div>
        </div>
        <div class="flex-1">
          <Label>Dibayar</Label>
          <div class="text-desc font-mono">{(query.data?.total_paid ?? 0).toLocaleString("en-US") ?? "-"}</div>
        </div>
      </div>
      <Separator/>
      <div>
        <h3 class="mb-1 text-lg font-semibold leading-none">Item</h3>
        {#if query.data && !query.data.items?.length}
          <div class="px-3 py-4 text-center text-xs">Tidak ada data</div>
        {/if}
        {#each query.data?.items ?? [] as item (item.id)}
          <div class="border-b py-2">
            <div class="text-sm">{item.product?.name ?? item.product_name}</div>
            <div class="flex items-start gap-3 text-xs leading-none text-muted-foreground">
              <div>{item.quantity.toLocaleString("en-US")} {item.unit}</div>
              <div class="flex-1"></div>
              {#if item.discount_percentage === 100}
                <div>-</div>
              {:else if item.price === 0}
                <div>{item.price.toLocaleString("en-US")}</div>
              {:else}
                <div class="text-end">
                  <div>{item.price.toLocaleString("en-US")}</div>
                  <div class="text-muted-foreground">
                    <span class="font-semibold text-primary">{item.discount_percentage}%</span>
                    <span class="line-through">{item.price_raw.toLocaleString("en-US")}</span>
                  </div>
                </div>
              {/if}
              <div class="w-24 text-end font-mono">
                {item.total > 0 ? item.total.toLocaleString("en-US") : "Free"}
              </div>
            </div>
          </div>
        {/each}
      </div>
    </div>
  </DialogContent>
</Dialog>
